<template>
<div class="container-fluid">
	<contact-toolbar v-if="$store.contact">

		<template slot="main">
			<div>
				<button class="btn btn-light" v-if="!hasAccountLink($store.contact)" v-on:click="inviteContactToPlatform">
					<i class="fa fa-paper-plane"></i>
					Invite to {{$store.appName}}...
				</button>
			</div>
		</template>

	</contact-toolbar>

	<div class="widgets">
		<widget>
			<h2 class="pl-2">
				{{$store.contact.name}}
				<small v-if="$store.contact.is_customer == 1">Customer</small>
				<small v-if="$store.contact.is_supplier == 1">Supplier</small>
			</h2>
		</widget>

		<alert
			:visible="$store.contact.customer_onhold == 1"
			:unclosable="true"
			classes="alert-warning"
			:message="onHoldMessage">
		</alert>
		
		<div class="row">
			<div class="col-md-6">
				<widget header-button="fa-pencil-alt" title="Contact Information" v-on:headerButtonClick="$router.push('/contact/'+$store.contact.slug+'/details')">
					<div class="row align-items-center">
						<div class="col-4">
							<h5 class="mb-1 mt-1">Address</h5>
						</div>
						<div class="col-8">
							<address class="mb-2 mt-2">
								<template v-if="$store.contact.suite">Suite {{$store.contact.suite}}<br></template>
								{{$store.contact.address}}<br>
								{{$store.contact.city}}, {{$store.contact.province}}, {{$store.contact.country}}<br>
								{{$store.contact.postal_code}}
							</address>
						</div>
					</div>

					<div class="row align-items-center">
						<div class="col-4">
							<h5>Phone</h5>
						</div>
						<div class="col-8">
							<div  class="mb-2 mt-2">
								<a :href="'tel:'+$store.contact.phone" >{{$store.contact.phone}}</a>
							</div>
						</div>
					</div>

					<div class="row align-items-center">
						<div class="col-4">
							<h5>Email</h5>
						</div>
						<div class="col-8">
							<div  class="mb-2 mt-2">
								<a :href="'mailto:'+$store.contact.email">{{$store.contact.email}}</a>
							</div>
						</div>
					</div>
				</widget>

				<widget title="Active Work" v-if="$store.contact.is_customer == 1">
					<div class="active-jobs-panel" v-on:click="activeProjectsClick">
						<h3 class="d-flex justify-content-between align-items-center">
							<div>
								<span class="total">{{numActiveProjects}} </span>
								<span>Active Projects</span>
							</div>
							<i class="fa fa-cubes"></i>
						</h3>
					</div>
					<div class="active-jobs-panel" v-on:click="activeServicesClick">
						<h3 class="d-flex justify-content-between align-items-center">
							<div>
								<span class="total">{{numActiveServices}} </span>
								<span>Active Service Jobs</span>
							</div>
							<i class="fa fa-wrench"></i>
						</h3>
					</div>
					<div class="active-jobs-panel" v-on:click="activeContractsClick">
						<h3 class="d-flex justify-content-between align-items-center">
							<div>
								<span class="total">{{numActiveServiceContracts}} </span>
								<span>Active Service Contracts</span>
							</div>
							<i class="fa fa-gavel"></i>
						</h3>
					</div>
				</widget>

				<widget title="Subcontacts" v-if="$store.contact.is_company == 1 && $store.contact.is_customer == 1">
					<contact-sub-contacts :is-sub-component="true"></contact-sub-contacts>
				</widget>

				<widget v-if="$store.contact.notes" title="Contact Notes">
					<pre>{{$store.contact.notes}}</pre>
				</widget>

				<widget title="Comments">
					<comment-thread v-if="$store.contact && 'slug' in $store.contact"
						class="mb-4"
						parent-model="Contact"
						:parent-slug="$store.contact.slug">
					</comment-thread>
				</widget>
			
			</div>
			<div class="col-md-6" v-if="$store.contact.is_customer == 0">
				<widget title="Subcontacts">
					<contact-sub-contacts :is-sub-component="true"></contact-sub-contacts>
				</widget>
			</div>
			<div class="col-md-6" v-if="$store.contact.is_customer == 1">
				<widget title="Revenue, Expenses & Labour by Month" v-if="'is_customer' in $store.contact && $store.contact.is_customer">

					<customer-revenue-graph interval="month"
						:aggregation-type="aggregationDisplayType"
						:customer-id="$store.contact.id"
						v-on:input="selectedChartPeriod = $event">

						<template slot="controls">
							<field label="Display"
								col-class="col-auto"
								:params="{type:'select',options: [
									{text: 'Cumulative',value:'cumulative'},
									{text: 'Monthly',value: 'interval-totaled'}
								]}"
								v-model="aggregationDisplayType">
							</field>
						</template>

					</customer-revenue-graph>

				</widget>

				<widget title="Project Revenue, Expenses & Labour by Month" v-if="'is_customer' in $store.contact && $store.contact.is_customer">

					<customer-revenue-graph interval="month"
						:sources="['projects']"
						aggregation-type="interval-totaled"
						:customer-id="$store.contact.id"
						:delay-fetch="delayReportFetch">
					</customer-revenue-graph>
				</widget>

				<widget title="Service Revenue, Expenses & Labour by Month" v-if="'is_customer' in $store.contact && $store.contact.is_customer">
					<customer-revenue-graph interval="month"
						:sources="['services']"
						aggregation-type="interval-totaled"
						:customer-id="$store.contact.id"
						:delay-fetch="delayReportFetch">
					</customer-revenue-graph>
				</widget>

				<widget title="Revenue, Expenses & Labour by Day" v-if="'is_customer' in $store.contact && $store.contact.is_customer">
					<customer-revenue-graph interval="day"
						aggregation-type="interval-totaled"
						:customer-id="$store.contact.id"
						:delay-fetch="delayReportFetch">
					</customer-revenue-graph>
				</widget>
			</div>
		</div>
	</div>

	<modal-dialog
		:modal-visible="inviteModal.visible"
		:title="'Invite to '+$store.appName"
		confirm-button-text="Send Invite"
		v-on:closeModal="inviteModal.visible = false"
		v-on:confirm="sendInvite">

		<p>This will send an invite to <a :href="'mailto:'+$store.contact.email">{{$store.contact.email}}</a> to join the {{$store.appName}} platform <template v-if="inviteModal.joinCompany">and to join {{$store.company.company_name}}</template></p>

		<div>
			<label v-on:click="inviteModal.joinCompany = true" v-on:change="inviteToggle" class="col-form-label">
				<input type="checkbox" v-model="inviteModal.joinCompany" value="1">
				Invite to {{$store.company.company_name}}
			</label>
		</div>

		<field label="Message"
			col-class="stacked"
			v-model="inviteModal.message"
			:params="{type:'textarea'}"
			v-on:change="inviteMessageChanged">
		</field>

	</modal-dialog>

</div>
</template>

<script>
import ContactToolbar from "./ContactToolbar"

import ModalDialog from "@/components/ModalDialog"
import Widget from "@/components/Widget"
import CommentThread from "@/components/super_components/CommentThread"
import InfoBox from "@/components/InfoBox"
import ContactSubContacts from "@/pages/contact/ContactSubContacts"
import Chart from "@/components/Chart"
import CustomerRevenueGraph from "@/components/super_components/CustomerRevenueGraph"
import Field from "@/components/Field"
import Alert from "@/components/Alert"

import ContactMixins from "@/mixins/ContactMixins"

export default {

	name: 'ContactSummary',
	components: {
		ContactToolbar,
		ModalDialog,
		Widget,
		CommentThread,
		ContactSubContacts,
		Chart,
		CustomerRevenueGraph,
		Field,
		Alert
	},
	mixins:[ContactMixins],
	data() {
		return {
			inviteModal: {
				visible: false,
				message: "",
				messageEdited: false
			},
			numActiveServices: null,
			numActiveProjects: null,
			numActiveServiceContracts: null,
			selectedChartPeriod: 'customer-lifespan',
			aggregationDisplayType: 'cumulative'
		}
	},
	computed: {
		onHoldMessage() {

			let onHoldReason = (this.$store.contact.customer_onhold_reason) ? this.$store.contact.customer_onhold_reason : ""

			return "<b>This Customer is On-Hold.</b>  No new project or services may be created " +
				"for this customer until this setting is turned off again.\n" +
				"Reason for On-Hold:   '" + onHoldReason + "'"
		}
	},
	methods: {
		inviteContactToPlatform() {
			this.inviteModal.visible = true
			this.inviteModal.message = "Hi, we'd like you to join "+this.$store.appName+"!"
			this.inviteModal.joinCompany = false
			this.inviteModal.messageEdited = false
		},
		sendInvite() {
			let vm = this

			let payload = {
				email: vm.$store.contact.email,
				message: vm.inviteModal.message,
				joinCompany: vm.inviteModal.joinCompany
			}

			this.postData("User/invite",payload,true).then(response => {
				if (response.success) {
					vm.inviteModal.visible = false
					vm.inviteModal.message = ""
					vm.alertSuccess("Invite sent!")
				}
			})
		},
		inviteMessageChanged() {
			this.inviteModal.messageEdited = true
		},
		inviteToggle() {
			if (!this.inviteModal.messageEdited) {
				if (this.inviteModal.joinCompany) 
					this.inviteModal.message = "Hi, we'd like you to join our company on "+this.$store.appName
				
				else 
					this.inviteModal.message = "Hi, we'd like you to join "+this.$store.appName+"!"
				
			}
		},
		activeProjectsClick() {
			this.$router.push({path:"/projects/all",query:{customer_id:this.$store.contact.id}})
		},
		activeServicesClick() {
			this.$router.push({path:"/services/all",query:{customer_id:this.$store.contact.id}})
		},
		activeContractsClick() {
			this.$router.push({path:"/service-contracts",query:{customer_id:this.$store.contact.id}})
		},
		fetchData() {
			let vm = this

			this.delayReportFetch = true

			let promises = [
				this.fetchContact(this.$route.params.id,["Contact","User","Company",{model:"ContactFile", fields:[]}])
			]

			this.spinner(true)

			Promise.all(promises).then(responses => {

				vm.delayReportFetch = false

				let serviceParams = {
					count: true,
					filters: [
						["archived",0],
						["customer_id",vm.$store.contact.id]
					]
				}

				this.getJSON("Service/get",serviceParams).then(response => {
					if (response.success)
						vm.numActiveServices = response.count
				})

				let projectParams = {
					count: true,
					filters: [
						["archived",0],
						["customer_id",vm.$store.contact.id]
					]
				}

				this.getJSON("Job/get",serviceParams).then(response => {
					if (response.success)
						vm.numActiveProjects = response.count
				})

				let contractParams = {
					count: true,
					filters: [
						["start_date <=",this.moment().format("YYYY-MM-DD")],
						["end_date >=",this.moment().format("YYYY-MM-DD")],
						["customer_id",vm.$store.contact.id]
					]
				}

				this.getJSON("ServiceContract/get",contractParams).then(response => {
					if (response.success)
						vm.numActiveServiceContracts = response.count
				})

				vm.spinner(false)
			})
		},
	},
	created() {
		this.fetchData()
	}
}
</script>

<style type="text/css">
	h2, address {
		text-transform: uppercase;
	}

	.active-jobs-panel {
		border: 1px solid #e0e0e0;
		padding: 15px;
		padding-top: 10px;
		padding-bottom: 10px;
		color: #50649c;
		margin-bottom: 0;
		margin-top: 5px;
		display: block;
		cursor: pointer;
	}

	.active-jobs-panel:hover {
		text-decoration: none;
		background-color: #e2e5ec;
	}

	.active-jobs-panel .total {
		font-size: 26pt;
		font-weight: bold;
	}

	.active-jobs-panel h3 {
		font-size: 14pt;
		color: #50649c;
		margin-bottom: 0;
	}

	@media (max-width: 550px) {
		.widgets {
			margin-left: 15px;
			margin-right: 15px;
		}
	}
</style>